import type { IPixCircle, IPixPath, IPixPolygon } from './types';
import { getBoundingBox } from './geo';

export interface BBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type Pos2 = [number, number];

// ---------------------------------------------------------------------------
// Low-level transforms
// ---------------------------------------------------------------------------

/** Flip [x, y] → [y, x] to match Pixi's coordinate conventions */
function flipPoint(p: number[]): Pos2 {
  return [p[1], p[0]];
}

/**
 * Clockwise rotation by angleDeg degrees in screen space (y increases downward).
 * Mathematically equivalent to counter-clockwise rotation in standard math convention.
 */
function rotatePoint(x: number, y: number, cx: number, cy: number, angleDeg: number): Pos2 {
  const rad = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = x - cx;
  const dy = y - cy;
  return [cx + cos * dx - sin * dy, cy + sin * dx + cos * dy];
}

/** Full forward transform: flip then rotate clockwise around center */
export function applyTransform(point: number[], center: Pos2, angleDeg: number): Pos2 {
  const [fx, fy] = flipPoint(point);
  return rotatePoint(fx, fy, center[0], center[1], angleDeg);
}

/** Inverse transform: un-rotate then un-flip – converts back to original model space */
export function invertTransform(point: Pos2, center: Pos2, angleDeg: number): Pos2 {
  const [ux, uy] = rotatePoint(point[0], point[1], center[0], center[1], -angleDeg);
  return [uy, ux]; // un-flip
}

// ---------------------------------------------------------------------------
// Batch transform of all viewer data
// ---------------------------------------------------------------------------

export interface TransformedPolygon extends IPixPolygon {
  tPoints: Pos2[]; // transformed points for deck.gl
}

export interface TransformedPath extends IPixPath {
  tPoints: Pos2[];
}

export interface TransformedCircle extends IPixCircle {
  tPos: Pos2;
}

export interface TransformedData {
  polygons: TransformedPolygon[];
  paths: TransformedPath[];
  circles: TransformedCircle[];
  /** Bounding box of all transformed polygon points */
  bbox: BBox;
  /** Rotation center (center of pre-rotation bounding box in flipped space) */
  center: Pos2;
  angleDeg: number;
}

export function computeTransformedData(
  polygons: IPixPolygon[],
  paths: IPixPath[],
  circles: IPixCircle[],
  angleDeg: number,
): TransformedData {
  // Compute rotation center from the bounding box of flipped (but not yet rotated) polygon points
  const allFlipped: Pos2[] = polygons.flatMap(p => p.points.map(flipPoint));
  const bbox0 = allFlipped.length > 0
    ? getBoundingBox(allFlipped)
    : { x: 0, y: 0, width: 0, height: 0 };
  const center: Pos2 = [bbox0.x + bbox0.width / 2, bbox0.y + bbox0.height / 2];

  const tPolygons: TransformedPolygon[] = polygons.map(p => ({
    ...p,
    tPoints: p.points.map(pt => applyTransform(pt, center, angleDeg)),
  }));

  const tPaths: TransformedPath[] = paths.map(p => ({
    ...p,
    tPoints: p.points.map(pt => applyTransform(pt, center, angleDeg)),
  }));

  const tCircles: TransformedCircle[] = circles.map(c => ({
    ...c,
    tPos: applyTransform(c.position, center, angleDeg),
  }));

  const allTPoints = tPolygons.flatMap(p => p.tPoints);
  const bbox = allTPoints.length > 0
    ? getBoundingBox(allTPoints)
    : { x: 0, y: 0, width: 1, height: 1 };

  return { polygons: tPolygons, paths: tPaths, circles: tCircles, bbox, center, angleDeg };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function computeFitZoom(bbox: BBox, containerW: number, containerH: number, paddingPx = 48): number {
  const uw = bbox.width;
  const uh = bbox.height;
  if (uw <= 0 || uh <= 0) return 0;
  const availW = Math.max(1, containerW - paddingPx * 2);
  const availH = Math.max(1, containerH - paddingPx * 2);
  return Math.log2(Math.min(availW / uw, availH / uh));
}

export function bboxCenter(bbox: BBox): Pos2 {
  return [bbox.x + bbox.width / 2, bbox.y + bbox.height / 2];
}

/** Area-weighted (shoelace) centroid. Assumes polygons are already closed
 *  (last point === first point), so iterates i < length - 1 to skip the duplicate.
 *  Translates to local origin first to avoid floating-point cancellation error at
 *  large world coordinates. */
export function polygonCentroid(points: Pos2[]): Pos2 {
  const n = points.length;
  if (n === 0) return [0, 0];
  // Translate to local origin using first point as reference
  const ox = points[0][0];
  const oy = points[0][1];
  let area = 0, x = 0, y = 0;
  for (let i = 0; i < n - 1; i++) {
    const ax = points[i][0] - ox,     ay = points[i][1] - oy;
    const bx = points[i+1][0] - ox,   by = points[i+1][1] - oy;
    const temp = ax * by - bx * ay;
    area += temp;
    x += (ax + bx) * temp;
    y += (ay + by) * temp;
  }
  area *= 0.5;
  if (Math.abs(area) < 1e-10) {
    // Degenerate — fall back to bounding-box center
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [px, py] of points) {
      if (px < minX) minX = px; if (px > maxX) maxX = px;
      if (py < minY) minY = py; if (py > maxY) maxY = py;
    }
    return [(minX + maxX) / 2, (minY + maxY) / 2];
  }
  return [ox + x / (6 * area), oy + y / (6 * area)];
}
